// noinspection ES6PreferShortImport -- IntelliJ recommends circular dependency

import { ChangeEventService } from '@faro/realtime-services';
import { createSafeContext } from '../../../util/createSafeContext';
import { ActivityService } from './Activity/ActivityService';
import { ActivityCostsExportCsvService } from './ActivityCosts/ActivityCostsExportCsvService';
import { AllocationActivityService } from './AllocationActivity/AllocationActivityService';
import { AllocationRuleService } from './AllocationRule/AllocationRuleService';
import { AllocationRuleAttributesService } from './AllocationRuleAttributes/AllocationRuleAttributesService';
import { AssessmentMeasurementConfigurationService } from './AssessmentMeasurementConfiguration/AssessmentMeasurementConfigurationService';
import { AssessmentMeasurementMetadataService } from './AssessmentMeasurementMetadata/AssessmentMeasurementMetadataService';
import { AssessmentMeasurementOptionsService } from './AssessmentMeasurementOptions/AssessmentMeasurementOptionsService';
import { AssessmentMeasurementSystemOptionsService } from './AssessmentMeasurementSystemOptions/AssessmentMeasurementSystemOptionsService';
import { AssessmentMeasurementVersionService } from './AssessmentMeasurementVersion/AssessmentMeasurementVersionService';
import { BiomedicalAssessmentMeasurementService } from './BiomedicalAssessmentMeasurement/BiomedicalAssessmentMeasurementService';
import { ClassTypeService } from './ClassType/ClassTypeService';
import { CountryService } from './Country/CountryService';
import { CycleService } from './Cycle/CycleService';
import { CycleGroupService } from './CycleGroup/CycleGroupService';
import { DiseaseAreaService } from './DiseaseArea/DiseaseAreaService';
import { DoseFormulationService } from './DoseFormulation/DoseFormulationService';
import { DoseRelativeUnitService } from './DoseRelativeUnit/DoseRelativeUnitService';
import { DynamicFieldService } from './DynamicField/DynamicFieldService';
import { EndpointService } from './Endpoint/EndpointService';
import { EpochGroupService } from './EpochGroup/EpochGroupService';
import { EpochTypeService } from './EpochType/EpochTypeService';
import { EstimandService } from './Estimand/EstimandService';
import { FeatureFlagService } from './FeatureFlag/FeatureFlagService';
import { InsightsExportImagesService } from './InsightsExportImageService/InsightsExportImagesService';
import { InterventionService } from './Intervention/InterventionService';
import { InterventionGroupService } from './InterventionGroup/InterventionGroupService';
import { InterventionGroupSequenceService } from './InterventionGroupSequence/InterventionGroupSequenceService';
import { InterventionManufacturerService } from './InterventionManufacturer/InterventionManufacturerService';
import { InterventionRouteOfAdministrationService } from './InterventionRouteOfAdministration/InterventionRouteOfAdministrationService';
import { InterventionSourcingService } from './InterventionSourcing/InterventionSourcingService';
import { InterventionUseService } from './InterventionUse/InterventionUseService';
import { LocationService } from './Location/LocationService';
import { ObjectiveService } from './Objective/ObjectiveService';
import { ObjectiveGroupService } from './ObjectiveGroup/ObjectiveGroupService';
import { ObjectiveGroupSequenceService } from './ObjectiveGroupSequence/ObjectiveGroupSequenceService';
import { ObjectiveTypeService } from './ObjectiveType/ObjectiveTypeService';
import { ScheduledDayService } from './ScheduledDay/ScheduledDayService';
import { ScheduleOfActivitiesService } from './ScheduleOfActivities/ScheduleOfActivitiesService';
import { ScheduleOfActivitiesSectionService } from './ScheduleOfActivitiesSection/ScheduleOfActivitiesSectionService';
import { SchemaService } from './Schema/SchemaService';
import { StageService } from './Stage/StageService';
import { StageGroupService } from './StageGroup/StageGroupService';
import { StudyService } from './Study/StudyService';
import { StudyAssessmentMeasurementService } from './StudyAssessmentMeasurement/StudyAssessmentMeasurementService';
import { StudyAssessmentMeasurementSystemOptionsService } from './StudyAssessmentMeasurementSystemOptions/StudyAssessmentMeasurementSystemOptionsService';
import { StudyContributorService } from './StudyContributor/StudyContributorService';
import { StudyEpochService } from './StudyEpoch/StudyEpochService';
import { StudyPhaseService } from './StudyPhase/StudyPhaseService';
import { StudyPopulationService } from './StudyPopulation/StudyPopulationService';
import { StudyRevisionService } from './StudyRevision/StudyRevisionService';
import { SubStudyService } from './SubStudy/SubStudyService';
import { SystemConfigService } from './SystemConfig/SystemConfigService';
import { TextSnippetService } from './TextSnippet/TextSnippetService';
import { TextSnippetOptionsService } from './TextSnippetOptions/TextSnippetOptionsService';
import { TherapeuticAreaService } from './TherapeuticArea/TherapeuticAreaService';
import { TimeUnitService } from './TimeUnit/TimeUnitService';
import { TitleContentService } from './TitleContent/TitleContentService';
import { UserCommentService } from './UserComment/UserCommentService';
import { UserInfoService } from './UserInfo/UserInfoService';
import { VariantService } from './Variant/VariantService';

/**
 * This context functions as our service registry, allowing consuming components to access services.
 */
export interface ServiceContextValue {
    activityService: ActivityService;
    allocationActivityService: AllocationActivityService;
    allocationRuleAttributesService: AllocationRuleAttributesService;
    allocationRuleService: AllocationRuleService;
    assessmentMeasurementOptionsService: AssessmentMeasurementOptionsService;
    assessmentMeasurementService: BiomedicalAssessmentMeasurementService;
    assessmentMeasurementConfigurationService: AssessmentMeasurementConfigurationService;
    assessmentMeasurementMetadataService: AssessmentMeasurementMetadataService;
    assessmentMeasurementSystemOptionsService: AssessmentMeasurementSystemOptionsService;
    assessmentMeasurementVersionService: AssessmentMeasurementVersionService;
    changeEventService: ChangeEventService;
    classTypeService: ClassTypeService;
    countryService: CountryService;
    cycleService: CycleService;
    cycleGroupService: CycleGroupService;
    diseaseAreaService: DiseaseAreaService;
    dynamicFieldService: DynamicFieldService;
    endpointService: EndpointService;
    epochGroupService: EpochGroupService;
    epochTypeService: EpochTypeService;
    estimandService: EstimandService;
    featureFlagService: FeatureFlagService;
    interventionDoseFormulationService: DoseFormulationService;
    interventionDoseRelativeUnitService: DoseRelativeUnitService;
    interventionGroupSequenceService: InterventionGroupSequenceService;
    interventionGroupService: InterventionGroupService;
    interventionManufacturerService: InterventionManufacturerService;
    interventionRouteOfAdministrationService: InterventionRouteOfAdministrationService;
    interventionSourcingService: InterventionSourcingService;
    interventionUseService: InterventionUseService;
    locationService: LocationService;
    objectiveGroupSequenceService: ObjectiveGroupSequenceService;
    objectiveGroupService: ObjectiveGroupService;
    objectiveService: ObjectiveService;
    objectiveTypeService: ObjectiveTypeService;
    scheduleOfActivitiesService: ScheduleOfActivitiesService;
    scheduledDayService: ScheduledDayService;
    schemaService: SchemaService;
    stageGroupService: StageGroupService;
    stageService: StageService;
    studyAssessmentMeasurementService: StudyAssessmentMeasurementService;
    studyAssessmentMeasurementSystemOptionsService: StudyAssessmentMeasurementSystemOptionsService;
    studyContributorService: StudyContributorService;
    studyEpochService: StudyEpochService;
    studyInterventionService: InterventionService;
    studyPhaseService: StudyPhaseService;
    studyPopulationService: StudyPopulationService;
    studyRevisionService: StudyRevisionService;
    studyService: StudyService;
    subStudyService: SubStudyService;
    systemConfigService: SystemConfigService;
    textSnippetOptionsService: TextSnippetOptionsService;
    textSnippetService: TextSnippetService;
    therapeuticAreaService: TherapeuticAreaService;
    timeUnitService: TimeUnitService;
    userCommentService: UserCommentService;
    userInfoService: UserInfoService;
    variantService: VariantService;
    activityCostsExportService: ActivityCostsExportCsvService;
    insightsImagesExportService: InsightsExportImagesService;
    scheduleOfActivitiesSectionService: ScheduleOfActivitiesSectionService;
    titleContentService: TitleContentService;
}

export const [ServiceContextValueProvider, useServiceContext, ServiceContext] =
    createSafeContext<ServiceContextValue>('StudyDesignerService');
